import React, { useState, useRef, useEffect } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { motion, AnimatePresence } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api } from "@/lib/api";
import {
  Mail,
  ArrowRight,
  Loader2,
  ShieldCheck,
  RotateCcw,
  X,
} from "lucide-react";

/**
 * PC2026 public landing / sign-in page.
 * The landing now displays the APPROVED original artwork
 * (/brand/pc2026-landing.png) exactly as provided — no recreation, cropping,
 * stretching, filters or substituted fonts/icons. A transparent, keyboard-
 * accessible button is positioned proportionally over the artwork's existing
 * "SIGN IN TO CONTINUE" area and opens the UNCHANGED Clerk email+OTP flow
 * (SignInModal + handlers below).
 */
const RESEND_SECONDS = 30;

// Transparent hit-area over the artwork's "SIGN IN TO CONTINUE" pill.
// Measured from the original 780x1688 mobile asset, expressed as percentages of
// the image wrapper so it stays aligned at every width (<1024px layout).
const SIGNIN_HOTSPOT = { left: "7.69%", top: "75.83%", width: "84.49%", height: "6.04%" };

// Measured from the approved 2000x1125 desktop asset, as percentages of the
// rendered desktop image (>=1024px layout) so it stays aligned on resize.
const SIGNIN_HOTSPOT_DESKTOP = { left: "72.60%", top: "40.89%", width: "24.10%", height: "6.58%" };

// App-level messages only — raw Clerk/API errors are never shown to attendees.
const MSG = {
  invalidEmail: "Please enter a valid email address.",
  notFound:
    "We couldn't find this email on the President's Club 2026 attendee list. Please use the email address you registered with or contact the Events team.",
  startError:
    "We couldn't start sign-in right now. Please try again in a moment, or contact the Events team.",
  codeShort: "Enter the 6-digit code from your email.",
  codeIncorrect: "That code isn't correct. Please check the 6-digit code and try again.",
  codeGeneric: "We couldn't verify that code. Request a new one and try again.",
  resendError: "We couldn't resend the code just now. Please try again shortly.",
};

export default function SignIn() {
  const { isLoaded, signIn, setActive } = useSignIn();

  const [open, setOpen] = useState(false); // sign-in modal
  const [stage, setStage] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (stage === "code" && codeRef.current) codeRef.current.focus();
      else if (stage === "email" && emailRef.current) emailRef.current.focus();
    }, 120);
    return () => clearTimeout(t);
  }, [stage, open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // ================= EXISTING CLERK FLOW — UNCHANGED =================
  async function startEmailCode(cleaned) {
    // User is confirmed eligible + provisioned server-side, so the sign-IN
    // (identifier) path applies. We never call Clerk sign-up from the client.
    await signIn.create({ identifier: cleaned });
    const factor = signIn.supportedFirstFactors?.find((f) => f.strategy === "email_code");
    if (!factor?.emailAddressId) throw new Error("email_code unavailable");
    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: factor.emailAddressId,
    });
  }

  async function sendCode(e) {
    if (e) e.preventDefault();
    if (!isLoaded || busy) return;
    const cleaned = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) {
      setError(MSG.invalidEmail);
      return;
    }
    setError("");
    setBusy(true);
    try {
      // 1) Invite-only eligibility pre-check (also provisions eligible users).
      let result;
      try {
        result = await api.eligibility(cleaned);
      } catch (apiErr) {
        setError(MSG.startError);
        setBusy(false);
        return;
      }
      if (!result?.eligible) {
        setError(MSG.notFound);
        setBusy(false);
        return;
      }
      // 2) Begin the Clerk email-code sign-in.
      await startEmailCode(cleaned);
      setStage("code");
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      // Never surface raw Clerk errors.
      setError(MSG.startError);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e) {
    if (e) e.preventDefault();
    if (!isLoaded || busy) return;
    if (!/^\d{6}$/.test(code)) {
      setError(MSG.codeShort);
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await signIn.attemptFirstFactor({ strategy: "email_code", code });
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.assign("/");
        return;
      }
      setError(MSG.codeGeneric);
    } catch (err) {
      const codeStr = err?.errors?.[0]?.code;
      setError(codeStr === "form_code_incorrect" ? MSG.codeIncorrect : MSG.codeGeneric);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (resendIn > 0 || busy || !isLoaded) return;
    setBusy(true);
    setError("");
    try {
      const factor = signIn.supportedFirstFactors?.find((f) => f.strategy === "email_code");
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: factor.emailAddressId,
      });
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      setError(MSG.resendError);
    } finally {
      setBusy(false);
    }
  }
  // ==================================================================

  function restart() {
    setStage("email");
    setCode("");
    setError("");
  }

  function openSignIn() {
    setError("");
    setOpen(true);
  }

  return (
    // Dark matching background around the artwork. Two approved images are
    // swapped purely by viewport width: the mobile portrait asset below 1024px
    // (unchanged), and the desktop landscape asset at 1024px and above. Only the
    // visible layout's button is in the DOM flow (the other is display:none, so
    // it is not focusable or announced).
    <div className="min-h-[100svh] w-full bg-[hsl(var(--pc-ink))]">
      {/* ---------- MOBILE / TABLET (<1024px) — existing approved asset ---------- */}
      <div className="flex min-h-[100svh] w-full justify-center bg-[hsl(var(--pc-ink))] lg:hidden">
        {/* Caps at the asset's native width so it is never upscaled; below that it
            scales down responsively (width:100%, height:auto). Vertical scroll
            when taller than the viewport — never cropped or forced to fit. */}
        <div className="relative w-full max-w-[780px]">
          <img
            src="/brand/pc2026-landing.png"
            alt="President's Club 2026 by Collabera — Curated for the Exceptional. Celebrate, Connect, Explore. December 9 to 13, 2026, Los Cabos, Mexico."
            data-testid="landing-artwork"
            className="block h-auto w-full select-none"
            draggable="false"
          />
          {/* Transparent, keyboard-accessible sign-in button over the artwork's
              "SIGN IN TO CONTINUE" area. Proportional to the rendered image. */}
          <button
            type="button"
            data-testid="landing-sign-in-button"
            onClick={openSignIn}
            aria-label="Sign in to continue"
            style={SIGNIN_HOTSPOT}
            className="absolute cursor-pointer rounded-full bg-transparent focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[hsl(var(--pc-gold))]"
          />
        </div>
      </div>

      {/* ---------- DESKTOP (>=1024px) — approved desktop asset ---------- */}
      {/* Centred and sized to fit BOTH available width and height (object stays
          whole, never cropped/stretched); surrounding space is the matching
          dark background. The wrapper shrink-wraps the rendered image so the
          overlay button is proportional to the image, not the viewport. */}
      <div className="hidden min-h-[100svh] w-full items-center justify-center bg-[hsl(var(--pc-ink))] lg:flex">
        <div className="relative inline-block leading-[0]">
          <img
            src="/brand/pc2026-landing-desktop.webp"
            alt="President's Club 2026 by Collabera — Curated for the Exceptional. Celebrate, Connect, Explore. December 9 to 13, 2026, Los Cabos, Mexico. Sign in to continue."
            data-testid="landing-artwork-desktop"
            className="block h-auto max-h-[100svh] w-auto max-w-[100vw] select-none"
            draggable="false"
          />
          <button
            type="button"
            data-testid="landing-sign-in-button-desktop"
            onClick={openSignIn}
            aria-label="Sign in to continue"
            style={SIGNIN_HOTSPOT_DESKTOP}
            className="absolute cursor-pointer rounded-full bg-transparent focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[hsl(var(--pc-gold))]"
          />
        </div>
      </div>

      {/* ============ SIGN-IN MODAL (email + OTP) ============ */}
      <SignInModal
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setError("");
        }}
        stage={stage}
        email={email}
        setEmail={setEmail}
        code={code}
        setCode={setCode}
        error={error}
        busy={busy}
        isLoaded={isLoaded}
        resendIn={resendIn}
        sendCode={sendCode}
        verifyCode={verifyCode}
        resend={resend}
        restart={restart}
        codeRef={codeRef}
        emailRef={emailRef}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sign-in modal — glass card over the immersive scene.               */
/* Uses Radix Dialog primitives directly for a lighter, blurred       */
/* overlay so the destination stays visible behind the card.          */
/* ------------------------------------------------------------------ */
function SignInModal({
  open,
  onOpenChange,
  stage,
  email,
  setEmail,
  code,
  setCode,
  error,
  busy,
  isLoaded,
  resendIn,
  sendCode,
  verifyCode,
  resend,
  restart,
  codeRef,
  emailRef,
}) {
  const inputBase =
    "w-full rounded-xl bg-[hsl(var(--pc-surface)/0.45)] border border-[hsl(var(--pc-line)/0.9)] text-cream placeholder:text-[hsl(var(--pc-cream)/0.4)] outline-none focus-visible:border-[hsl(var(--pc-gold))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold)/0.35)] transition-colors";
  const primaryBtn =
    "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--pc-gold))] px-5 py-3.5 font-semibold uppercase tracking-[0.1em] text-[hsl(var(--pc-ink))] shadow-lg transition-[filter,opacity] duration-200 hover:brightness-105 active:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--pc-ink))]";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[hsl(var(--pc-ink)/0.62)] backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="signin-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[30rem] -translate-x-1/2 -translate-y-1/2 focus:outline-none"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="att-card att-glass relative rounded-[22px] p-6 text-cream shadow-[0_24px_80px_hsl(var(--pc-ink)/0.65)] sm:p-8"
          >
            {/* Close */}
            <DialogPrimitive.Close
              data-testid="signin-close-button"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-[hsl(var(--pc-cream)/0.7)] transition-colors duration-200 hover:bg-[hsl(var(--pc-surface2)/0.7)] hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold)/0.5)]"
              aria-label="Close sign in"
            >
              <X className="h-4.5 w-4.5" />
            </DialogPrimitive.Close>

            {/* Badge + heading */}
            <div className="flex flex-col items-center text-center">
              <img
                src="/brand/pc2026-badge.png"
                alt="President's Club 2026"
                className="h-16 w-16"
              />
            </div>

            <AnimatePresence mode="wait">
              {stage === "email" ? (
                <motion.form
                  key="email"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={sendCode}
                  className="mt-5 space-y-5"
                  data-testid="signin-email-form"
                >
                  <div className="text-center">
                    <DialogPrimitive.Title className="font-serif text-3xl leading-tight text-cream">
                      Welcome
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="mt-2 text-[15px] leading-relaxed text-cream-dim">
                      Enter the email address you registered for President&apos;s Club 2026.
                    </DialogPrimitive.Description>
                  </div>

                  <div>
                    <label
                      htmlFor="signin-email"
                      className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-cream-dim"
                    >
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-[hsl(var(--pc-gold))]" />
                      <input
                        id="signin-email"
                        ref={emailRef}
                        data-testid="signin-email-input"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@collabera.com"
                        className={`${inputBase} py-3.5 pl-11 pr-4`}
                      />
                    </div>
                  </div>

                  {error && (
                    <p
                      className="rounded-xl border border-[hsl(8_60%_60%/0.4)] bg-[hsl(8_60%_50%/0.12)] px-4 py-3 text-sm leading-relaxed text-[hsl(8_75%_82%)]"
                      role="alert"
                      data-testid="signin-error"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="signin-continue-button"
                    disabled={busy || !isLoaded}
                    className={primaryBtn}
                  >
                    {busy ? (
                      <Loader2 className="h-4.5 w-4.5 animate-spin" />
                    ) : (
                      <>
                        Continue <ArrowRight className="h-4.5 w-4.5" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs leading-relaxed text-[hsl(var(--pc-cream)/0.45)]">
                    Access is limited to registered President&apos;s Club 2026 attendees.
                  </p>
                </motion.form>
              ) : (
                <motion.form
                  key="code"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={verifyCode}
                  className="mt-5 space-y-5"
                  data-testid="signin-code-form"
                >
                  <div className="text-center">
                    <div className="mb-2 inline-flex items-center gap-2 text-[hsl(var(--pc-gold))]">
                      <ShieldCheck className="h-5 w-5" />
                      <span className="text-sm font-semibold uppercase tracking-[0.12em]">
                        Verify your email
                      </span>
                    </div>
                    <DialogPrimitive.Title className="sr-only">
                      Verify your email
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-[15px] leading-relaxed text-cream-dim">
                      We&apos;ve sent a secure sign-in code to{" "}
                      <span className="font-semibold text-cream">{email}</span>
                    </DialogPrimitive.Description>
                  </div>

                  <div>
                    <label
                      htmlFor="signin-code"
                      className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-cream-dim"
                    >
                      Verification code
                    </label>
                    <input
                      id="signin-code"
                      ref={codeRef}
                      data-testid="signin-code-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      className={`${inputBase} px-4 py-4 text-center font-serif text-2xl tracking-[0.6em]`}
                    />
                  </div>

                  {error && (
                    <p
                      className="rounded-xl border border-[hsl(8_60%_60%/0.4)] bg-[hsl(8_60%_50%/0.12)] px-4 py-3 text-sm leading-relaxed text-[hsl(8_75%_82%)]"
                      role="alert"
                      data-testid="signin-error"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="signin-verify-button"
                    disabled={busy || !isLoaded}
                    className={primaryBtn}
                  >
                    {busy ? (
                      <Loader2 className="h-4.5 w-4.5 animate-spin" />
                    ) : (
                      <>
                        Verify &amp; continue <ArrowRight className="h-4.5 w-4.5" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      data-testid="signin-resend-button"
                      onClick={resend}
                      disabled={resendIn > 0 || busy}
                      className="inline-flex items-center gap-1.5 text-cream-dim transition-colors duration-200 hover:text-cream disabled:opacity-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                    </button>
                    <button
                      type="button"
                      data-testid="signin-change-email-button"
                      onClick={restart}
                      className="text-cream-dim underline underline-offset-2 transition-colors duration-200 hover:text-cream"
                    >
                      Use a different email
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
